// =============================================================================
// IMAGE SHOWCASE COMPONENT
// =============================================================================

import React from 'react';
import { Button } from '@/components/Button';
import { createActionHandler, convertButtonToAction } from '@/utils/actionHandler';
import type { ContentComponent } from '@/types';

interface ImageShowcaseProps {
  component: ContentComponent;
}

/**
 * Image Showcase Component
 */
export const ImageShowcase: React.FC<ImageShowcaseProps> = ({ component }) => {
  return <ImageShowcaseImpl component={component} />;
};

/**
 * Image Showcase Implementation
 */
const ImageShowcaseImpl: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const buttons = metadata?.buttons || [];
  const backgroundImage = metadata?.backgroundImage || metadata?.image;
  const overlayOpacity = metadata?.overlayOpacity || 0.6;
  const textAlign = metadata?.textAlign || 'center';
  const minHeight = metadata?.minHeight || '92vh ';
  const overlayColor = metadata?.overlayColor || 'rgba(0, 0, 0, 0.6)';
  const textColor = metadata?.textColor || 'white';
  const showOverlay = metadata?.showOverlay !== false;
  
  const actions = buttons.map(convertButtonToAction);
  
  return (
    <section
      id={id}
      className={`image-showcase ${backgroundImage ? 'has-background-image' : ''}`}
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
        minHeight: minHeight,
        textAlign: textAlign as React.CSSProperties['textAlign']
      }}
    >
      {/* Background Overlay */}
      {showOverlay && (
        <div
          className="image-showcase-overlay"
          style={{
            opacity: overlayOpacity,
            backgroundColor: overlayColor
          }}
        />
      )}
      
      {/* Content Container */}
      <div className="image-showcase-content">
        <div className="container">
          <div
            className="image-showcase-text"
            style={{ color: textColor }}
          >
            {title && <h2 className="image-showcase-title">{title}</h2>}
            {content && <p className="image-showcase-description">{content}</p>}
            
            {actions.length > 0 && (
              <div className="image-showcase-actions">
                {actions.map((action, index) => (
                  <Button
                    key={index}
                    variant={action.variant || 'primary'}
                    size={action.size || 'lg'}
                    onClick={createActionHandler(action)}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export { ImageShowcaseImpl };

